/**
 * The twelve, drawn — and it is two draw calls, not two hundred and fifty.
 *
 * ── Why this is not twelve `finisher-herd.create()` calls ───────────────────
 *
 * `tests/e2e/render-budget.spec.ts` says it in as many words: *"twelve finishers
 * as twelve separate meshes would be another ~470 draw calls, and the answer is
 * `instanceGroup`."* The hedgerow is the most expensive room in the game and its
 * ceiling was set from a measurement, not from an aspiration. So the twelve share
 * one `InstancedMesh` per surface — the same shape `henFlock.ts` uses, and for
 * the same reason — and every animal's transform is written into the per-instance
 * matrix each frame.
 *
 * ── What that costs, stated here rather than discovered later ───────────────
 *
 * **A herd pig has no moving parts.** `mergeBySurface` bakes the whole subtree
 * into one geometry per surface, so a leg cannot swing and an ear cannot flick;
 * `finisher-herd` says so at its own definition and exports no rig node to make
 * the point structural. Everything alive about these animals therefore lives in
 * the root transform: a gait bob, a small roll, and the heading. That was enough
 * for eight panicking hens in a lit corridor at three metres, and it is being
 * asked to do more here — twelve walking animals in moonlight — so the frames are
 * the check and not this comment.
 *
 * ── Simulation writes state, presentation reads it ──────────────────────────
 *
 * Nothing in this file decides anything. `CulvertQueue` owns every distance,
 * every state and the clock; this reads a snapshot and puts bodies where it says.
 * The one piece of state it keeps is the per-animal scatter timer, which is a
 * render-rate animation for an event the queue has already decided, in the same
 * way the camera keeps its own spring.
 */

import * as THREE from 'three';
import { getAsset } from '@/models/registry';
import { HERD_SIZE, type CulvertQueueSnapshot, type MemberState } from '@/story/act2/culvertQueue';
import { HERD_HUDDLE_AT, culvertRoutePointAt, hedgerowGroundY } from '@/world/hedgerow';
import { mergeBySurface } from '@/world/instancing';

/**
 * Where each of the twelve stands in the huddle, in metres from `HERD_HUDDLE_AT`.
 *
 * **Authored, not seeded**, and that is a departure from `henFlock.ts` on
 * purpose. The flock is dressing that moves and any twelve positions would have
 * done; this huddle is *the only number the beat ever gives the player* — spec
 * §7.10 asks for a headcount taken by looking — so twelve overlapping backs are
 * not a cosmetic problem, they are the mechanic failing. A rejection-sampled
 * scatter gets a minimum separation and nothing else; what is wanted is twelve
 * separable silhouettes **from where she stands when she looks**, which is up
 * the field at (0, 6) looking down it.
 *
 * Hence the shape: **wide across her view and shallow along it** — 4.85 m of x
 * against 2.55 m of z — so the depth axis, which is the one that stacks animals
 * on top of each other, carries as few of them as possible. Minimum separation is
 * 0.85 m against a body 1.15 m long and 0.34 m wide, which reads as a huddle
 * rather than a queue and still resolves at 14 m.
 */
const HUDDLE_OFFSETS: readonly { readonly x: number; readonly z: number; readonly turn: number }[] = [
  { x: -2.35, z: 0.55, turn: -0.22 },
  { x: -1.65, z: -0.35, turn: 0.35 },
  { x: -0.95, z: 0.85, turn: 0.10 },
  { x: -0.30, z: -0.10, turn: -0.40 },
  { x: 0.35, z: 0.95, turn: 0.28 },
  { x: 1.05, z: -0.55, turn: -0.15 },
  { x: 1.80, z: 0.35, turn: 0.42 },
  { x: 2.50, z: -0.25, turn: -0.30 },
  { x: -1.95, z: -1.35, turn: 0.18 },
  { x: -0.60, z: -1.55, turn: -0.48 },
  { x: 0.75, z: -1.35, turn: 0.24 },
  { x: 2.05, z: -1.60, turn: -0.12 },
];

/**
 * Metres of route over which an animal stops being part of a huddle and becomes
 * part of a file.
 *
 * At distance 0 the route's own first point IS the huddle centre, so without a
 * blend every animal would snap from its authored offset onto the line the
 * instant the string moved. Two metres is long enough that the break reads as a
 * herd gathering itself and short enough that they are a file before the bank.
 */
const HUDDLE_BREAK = 2.0;

/** Half the lateral spread of the file, metres. A pig is 0.34 m wide; they walk two abreast-ish. */
const FILE_HALF_WIDTH = 0.34;

/**
 * Metres between the animals BACKED UP at the pipe, and it is presentation only.
 *
 * ── The stage called "the queue" rendered a pile ────────────────────────────
 *
 * `CulvertQueue.#distanceOf` clamps every arrived animal to `routeLength`, which
 * is correct — a queued animal is at the mouth and there is nothing past it to
 * be at — but it means `queued` and `passing` members all report the SAME
 * distance. Drawn straight, that put them at one point with only the ±file
 * parity between them: **two rows of up to six coincident bodies, peaking at
 * eleven animals in two positions at t = 18.8 s and holding near that until the
 * terminal at 52 s.** Over half the stage's runtime, in the stage whose whole
 * subject is a queue. An independent review measured it.
 *
 * The fix is entirely on this side of the seam. The module keeps reporting the
 * truth about who has arrived; the renderer backs the waiting ones up the route
 * behind the mouth in arrival order, which is what a queue at a pipe looks like.
 * **Nothing here feeds back into the simulation** — the count that gets out is
 * still a function of the input trace and this changes it by nothing.
 *
 * 1.05 m is just under `HERD_SPACING`, so the backlog reads slightly tighter
 * than the walking string: animals waiting to go through a hole stand closer
 * together than animals walking behind a leader.
 */
const QUEUE_BACKLOG_SPACING = 1.05;

/**
 * The gait, as a frequency and a height.
 *
 * `HERD_SPEED` is 1.55 m/s and a finishing pig's stride is about 0.7 m, so 2.2 Hz
 * is the cadence the legs would have if there were legs. Getting this wrong in
 * either direction is what makes an instanced animal read as a sliding prop: too
 * slow and it hovers, too fast and it vibrates.
 */
const GAIT_HERTZ = 2.2;
const GAIT_BOB = 0.022;
const GAIT_ROLL = 0.035;

/** Seconds the break-up takes once the dogs arrive, and how far it carries them. */
const SCATTER_SECONDS = 2.6;
const SCATTER_DISTANCE = 3.4;

/** An animal that is through the pipe is not in the field. Zero scale is how an instance leaves. */
const GONE = 0.0001;

interface Member {
  /** Which way this one breaks when the terminal fires. Authored from its huddle offset. */
  readonly scatterX: number;
  readonly scatterZ: number;
  /** Seconds since this animal first read as `scattered`. Presentation's own clock. */
  elapsed: number;
  /** Where it stood at the moment it scattered, so the drift starts from the body. */
  fromX: number;
  fromZ: number;
  seeded: boolean;
}

export class NightHuddle {
  readonly group: THREE.Group;
  readonly #meshes: THREE.InstancedMesh[] = [];
  readonly #members: Member[] = [];
  readonly #matrix = new THREE.Matrix4();
  readonly #position = new THREE.Vector3();
  readonly #quaternion = new THREE.Quaternion();
  readonly #euler = new THREE.Euler();
  readonly #scale = new THREE.Vector3(1, 1, 1);
  #clock = 0;

  constructor() {
    const asset = getAsset('finisher-herd');
    if (!asset) throw new Error('night huddle: the finisher-herd asset is not registered');

    this.group = new THREE.Group();
    this.group.name = 'hedgerow-night-huddle';

    for (const batch of mergeBySurface(asset.create())) {
      const mesh = new THREE.InstancedMesh(batch.geometry, batch.material, HERD_SIZE);
      mesh.name = `night-huddle-${batch.name}`;
      mesh.castShadow = batch.castShadow;
      mesh.receiveShadow = batch.receiveShadow;
      // They move, so a bounding sphere computed once is wrong by the second
      // frame — and the failure mode of getting this wrong is the whole herd
      // blinking out when the camera turns, which is `henFlock.ts`'s note.
      mesh.frustumCulled = false;
      this.#meshes.push(mesh);
      this.group.add(mesh);
    }

    for (let index = 0; index < HERD_SIZE; index += 1) {
      const offset = HUDDLE_OFFSETS[index];
      if (!offset) throw new Error('night huddle: HUDDLE_OFFSETS must cover every member');
      // Outward from the centre of the huddle. A herd of pigs standing in an open
      // field at night faces out, and it is also what keeps twelve bodies from
      // pointing lengthwise at each other and merging into four long shapes.
      const outward = Math.hypot(offset.x, offset.z);
      this.#members.push({
        scatterX: outward === 0 ? 0 : offset.x / outward,
        scatterZ: outward === 0 ? 1 : offset.z / outward,
        elapsed: 0,
        fromX: 0,
        fromZ: 0,
        seeded: false,
      });
    }

    // The huddle is posed at construction, not on the first `update`. An
    // `InstancedMesh` starts with identity matrices, so twelve pigs would spend
    // the boot frame stacked on the world origin — which in this room is up on
    // the apron, in shot, thirteen metres from where they belong.
    for (let index = 0; index < HERD_SIZE; index += 1) {
      const member = this.#members[index];
      const offset = HUDDLE_OFFSETS[index];
      if (!member || !offset) continue;
      this.#writeOne(index, member, offset, 'huddled', 0, 0, 0);
    }
    for (const mesh of this.#meshes) mesh.instanceMatrix.needsUpdate = true;
  }

  /** The huddle's authored centre, for a camera or an interactable to aim at. */
  static get centre(): { readonly x: number; readonly z: number } {
    return HERD_HUDDLE_AT;
  }

  update(frameDt: number, snapshot: CulvertQueueSnapshot): void {
    const dt = Number.isFinite(frameDt) ? Math.max(0, Math.min(0.1, frameDt)) : 0;
    this.#clock += dt;

    for (let index = 0; index < HERD_SIZE; index += 1) {
      const member = this.#members[index];
      const state = snapshot.members[index];
      const offset = HUDDLE_OFFSETS[index];
      if (!member || !state || !offset) continue;
      this.#writeOne(index, member, offset, state.state, state.distance, dt, this.#backlogRank(snapshot, index));
    }

    for (const mesh of this.#meshes) mesh.instanceMatrix.needsUpdate = true;
  }

  /**
   * How many animals are waiting AHEAD of this one at the mouth.
   *
   * `passing` is rank 0 — it is in the pipe — and each `queued` animal takes the
   * next place back. Derived from the snapshot every frame rather than stored,
   * so it cannot go stale against a queue that has moved on, and computed in
   * member order because `CulvertQueue`'s string arrives in index order. (Act 3's
   * ramp will not, and when it does not this wants the queue's own order
   * exposed rather than this loop guessing.)
   */
  #backlogRank(snapshot: CulvertQueueSnapshot, index: number): number {
    let rank = 0;
    for (let ahead = 0; ahead < HERD_SIZE; ahead += 1) {
      if (ahead === index) break;
      const state = snapshot.members[ahead]?.state;
      if (state === 'passing' || state === 'queued') rank += 1;
    }
    return rank;
  }

  #writeOne(
    index: number,
    member: Member,
    offset: { readonly x: number; readonly z: number; readonly turn: number },
    state: MemberState,
    distance: number,
    dt: number,
    backlogRank: number,
  ): void {
    if (state === 'out') {
      this.#hide(index);
      return;
    }

    // Waiting animals stand back up the route from the mouth in arrival order.
    // `passing` is rank 0 and is drawn at the mouth itself, which is where an
    // animal entering a pipe is. See `QUEUE_BACKLOG_SPACING`.
    const drawnDistance = state === 'queued' || state === 'passing'
      ? Math.max(0, distance - backlogRank * QUEUE_BACKLOG_SPACING)
      : distance;
    const point = culvertRoutePointAt(drawnDistance);
    // The file is a line, so twelve animals on it would be one animal from
    // behind. Alternating sides is the cheapest thing that keeps a string
    // countable, and it costs nothing that has to be simulated.
    const side = index % 2 === 0 ? 1 : -1;
    const fileX = Math.cos(point.heading) * side * FILE_HALF_WIDTH;
    const fileZ = -Math.sin(point.heading) * side * FILE_HALF_WIDTH;
    const blend = Math.max(0, Math.min(1, drawnDistance / HUDDLE_BREAK));
    let x = point.x + offset.x * (1 - blend) + fileX * blend;
    let z = point.z + offset.z * (1 - blend) + fileZ * blend;
    let heading = Math.atan2(offset.x, offset.z) + offset.turn;
    if (blend > 0) {
      // `atan2(sin, cos)` rather than a plain lerp: the huddle's outward facings
      // and the route's heading are three radians apart for the animals on the
      // far side, and a linear blend between them spins the wrong way round.
      const delta = Math.atan2(Math.sin(point.heading - heading), Math.cos(point.heading - heading));
      heading += delta * blend;
    }

    if (state === 'scattered') {
      if (!member.seeded) {
        member.seeded = true;
        member.elapsed = 0;
        member.fromX = x;
        member.fromZ = z;
      }
      member.elapsed = Math.min(SCATTER_SECONDS, member.elapsed + dt);
      // Ease out: they bolt and then stop, which is what an animal that has been
      // frightened by something it cannot see actually does.
      const t = member.elapsed / SCATTER_SECONDS;
      const eased = 1 - (1 - t) * (1 - t);
      x = member.fromX + member.scatterX * SCATTER_DISTANCE * eased;
      z = member.fromZ + member.scatterZ * SCATTER_DISTANCE * eased;
      heading = Math.atan2(member.scatterX, member.scatterZ);
    } else {
      member.seeded = false;
    }

    // The moving animals bob; the standing ones do not. A whole huddle breathing
    // in step is a worse artefact than a huddle that is still, and the phase is
    // the index rather than a random so that two runs of the beat agree (D-045).
    const walking = state === 'moving' || state === 'passing';
    const phase = this.#clock * Math.PI * 2 * GAIT_HERTZ + index * 0.7;
    const bob = walking ? Math.abs(Math.sin(phase)) * GAIT_BOB : 0;
    const roll = walking ? Math.sin(phase) * GAIT_ROLL : 0;

    this.#position.set(x, hedgerowGroundY(x, z) + bob, z);
    this.#euler.set(0, heading, roll, 'YXZ');
    this.#quaternion.setFromEuler(this.#euler);
    this.#scale.set(1, 1, 1);
    this.#matrix.compose(this.#position, this.#quaternion, this.#scale);
    for (const mesh of this.#meshes) mesh.setMatrixAt(index, this.#matrix);
  }

  #hide(index: number): void {
    // Scaled to nothing rather than moved away: an instance parked under the
    // terrain is still in the shadow map's bounds and still costs its triangles
    // in every pass this room's census counts.
    this.#position.set(HERD_HUDDLE_AT.x, hedgerowGroundY(HERD_HUDDLE_AT.x, HERD_HUDDLE_AT.z), HERD_HUDDLE_AT.z);
    this.#quaternion.identity();
    this.#scale.set(GONE, GONE, GONE);
    this.#matrix.compose(this.#position, this.#quaternion, this.#scale);
    for (const mesh of this.#meshes) mesh.setMatrixAt(index, this.#matrix);
  }

  /**
   * Where an animal actually is, read back off the instance matrix.
   *
   * **Not recomputed from the queue's distance**, and that is the whole reason
   * this method exists rather than a debug object built beside the snapshot.
   * `docs/traps.md`: *a debug value recomputed with the caller's own expression
   * agrees with itself* — beat 10 has already written that defect twice, one
   * file apart, and a herd gate fed by `culvertRoutePointAt(distance)` would
   * pass against a `NightHuddle` that drew nothing at all.
   */
  positionOf(index: number): { readonly x: number; readonly y: number; readonly z: number } {
    const mesh = this.#meshes[0];
    if (!mesh) throw new Error('night huddle: no instanced mesh to read back from');
    mesh.getMatrixAt(index, this.#matrix);
    this.#matrix.decompose(this.#position, this.#quaternion, this.#scale);
    return { x: this.#position.x, y: this.#position.y, z: this.#position.z };
  }

  /**
   * How many of the twelve are still drawn in the field, from the matrices.
   *
   * The counterpart to `positionOf`, and the assertion that an animal which is
   * `out` has actually left the frame. `#hide` scales an instance to nothing;
   * anything above that threshold is a body somebody can see.
   */
  get visible(): number {
    const mesh = this.#meshes[0];
    if (!mesh) return 0;
    let count = 0;
    for (let index = 0; index < HERD_SIZE; index += 1) {
      mesh.getMatrixAt(index, this.#matrix);
      this.#matrix.decompose(this.#position, this.#quaternion, this.#scale);
      if (this.#scale.x > GONE * 10) count += 1;
    }
    return count;
  }

  /**
   * Put the twelve back in the huddle, and clear the scatter timers with them.
   *
   * `TwelveOut.reset()`'s header is the argument: a reset that restores half of
   * a pair leaves the other half holding state from a run that no longer exists.
   * Here that is a member frozen three metres out from a bolt it made in the
   * previous life of the beat, standing in the field while the queue reports it
   * huddled. `respawn()` in `main.ts` calls this beside `CulvertQueue.reset()`.
   */
  reset(): void {
    this.#clock = 0;
    for (let index = 0; index < HERD_SIZE; index += 1) {
      const member = this.#members[index];
      const offset = HUDDLE_OFFSETS[index];
      if (!member || !offset) continue;
      member.elapsed = 0;
      member.fromX = 0;
      member.fromZ = 0;
      member.seeded = false;
      this.#writeOne(index, member, offset, 'huddled', 0, 0, 0);
    }
    for (const mesh of this.#meshes) mesh.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    for (const mesh of this.#meshes) {
      mesh.geometry.dispose();
      mesh.dispose();
    }
    this.#meshes.length = 0;
    this.group.clear();
    // `clear()` empties the group; it does not take the group out of the scene.
    // Without this, tearing the room down leaves an empty `hedgerow-night-huddle`
    // node behind — `henFlock.ts` detaches and this did not. Found by review.
    this.group.removeFromParent();
  }
}
